/**
 * The shared forecast (TMMAPP-0102 §7; P v2 L1171–1296): one card with a toolbar (horizon,
 * a filter slot, the window), a KPI row, the chart and a note, then the "Planned events"
 * list-table. The KPI set, the note and the filter are props, so the virtual account and envelope
 * pages reuse it. The chart and the list read the same projection response (MM-D-53); a drill-down
 * narrows the list only, and the parent re-fetches it with a narrower `from` / `to`.
 */
import { useState, type ReactNode } from 'react';
import { MoneyParts } from '@/components/MoneyText';
import { registerAmountColor } from '@/components/moneyTone';
import { Box, Button, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { EventView } from '@/api/projections';
import { ListTableHeader } from '@/components/ListTableHeader';
import { SegmentedControl } from '@/components/SegmentedControl';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useUrlState } from '@/shared/useUrlState';
import { resolveTokens } from '@/theme/theme';
import { HORIZONS, HORIZON_SCHEMA, type HorizonMonths } from './horizon';
import { ProjectionChart } from './ProjectionChart';
import type { ChartSeries, DrillDown } from './types';

export interface ForecastKpi {
  id: string;
  /** The KPI's `overline` label. */
  label: ReactNode;
  /** The figure (`figureSm` 26/300), rendered by the caller. */
  value: ReactNode;
  /** A 12px line under the figure (a delta, a cause). */
  note?: ReactNode;
}

/** The events list (the card's second block): the rows are the projection's own events. */
export interface ForecastEvents {
  rows: EventView[];
  /** The currency of the gross amount (the account's own). */
  amountCurrency: string;
  /** The currency of Allocated / Free / Unallocated (the operating currency, MM-D-63). */
  operatingCurrency: string | null;
  /**
   * A revolving or loan account (TMMAPP-0101): no allocations, so the list is Date · Event · Amount ·
   * Balance (the served `runningMinor`), every figure in the account's own currency.
   */
  balanceView?: boolean;
  /** The "Unallocated today" start row; `null` hides it, unless `startPending`. */
  startMinor: number | null;
  /**
   * The account's opening is pending (TMMAPP-0123 item 19a): the start is null on purpose, so the row
   * shows "—" rather than hiding.
   */
  startPending?: boolean;
  /** The start row's label (default "Unallocated today"). */
  startLabel?: ReactNode;
  /** The empty copy (default: nothing scheduled on this account in this window). */
  empty?: ReactNode;
  /** The list sub-line's count wording ("{n} events"). */
  count?: ReactNode;
  /** Opens a row; omit in a read-only surface. */
  onOpen?: ((event: EventView) => void) | undefined;
  /** A drill-down is narrowing the rows; "Show full window" clears it. */
  drilled?: boolean;
  onWiden?: (() => void) | undefined;
}

interface ForecastCardProps {
  /** The window the chart draws (the full horizon). */
  from: string;
  to: string;
  chart: {
    series: ChartSeries[];
    events: EventView[];
    register: Parameters<typeof ProjectionChart>[0]['register'];
    currency: string | null;
    minimum: { date: string; balanceMinor: number } | null;
    onDrill?: ((drill: DrillDown) => void) | undefined;
  };
  kpis: ForecastKpi[];
  /** The 12px note under the chart. */
  note?: ReactNode;
  /** The filter slot, after the horizon (the account tab's envelope filter). */
  filter?: ReactNode;
  /** The account's "Planned events" list; omit it when `list` replaces it. */
  events?: ForecastEvents;
  /**
   * A list of the caller's own in place of "Planned events" (TMMAPP-0108: the virtual account's
   * "Cashflow forecast" list with allocation chips).
   */
  list?: ReactNode;
  /** An accessible name for the card's region. */
  label: string;
  /**
   * The list's window was served from today, later than it asked (TMMAPI-0188 U3): a "From today"
   * caption heads the list (TMMAPP-0159 item 26).
   */
  fromToday?: boolean;
}

const DATE_SHORT = { month: 'short', day: 'numeric' } as const;

function Overline({ children }: { children: ReactNode }) {
  const tokens = resolveTokens(useTheme());
  return (
    <Typography
      variant="overline"
      component="span"
      sx={{ color: tokens.textLabel('surface'), whiteSpace: 'nowrap' }}
    >
      {children}
    </Typography>
  );
}

export function ForecastCard({
  from,
  to,
  chart,
  kpis,
  note,
  filter,
  events,
  list,
  label,
  fromToday = false,
}: ForecastCardProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const [{ h: horizon }, setUrl] = useUrlState(HORIZON_SCHEMA);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());

  function toggleSeries(id: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const money = (minor: number | null, currency: string | null): ReactNode =>
    minor === null ? '—' : <MoneyParts minor={minor} currency={currency} />;
  // The planned events are a projected register (contract v2 §13, TMMAPP-0154 §A 1): an event's
  // amount and its free part are list amounts (outgoing ink with a true minus, incoming
  // `moneyPositive` with "+"); the start and the running balances are balances (`text`, and
  // `moneyNegative` below zero), never green.
  const signedMoney = (minor: number | null, currency: string | null): ReactNode =>
    minor === null ? '—' : <MoneyParts minor={minor} currency={currency} signed />;
  const amountTone = (minor: number | null) =>
    minor === null ? tokens.text2 : registerAmountColor(tokens, minor);
  const balanceTone = (minor: number | null) =>
    minor === null ? tokens.text2 : minor < 0 ? tokens.moneyNegative : tokens.text;

  function eventsList(events: ForecastEvents) {
    const balanceView = events.balanceView === true;
    const columns = balanceView
      ? '56px minmax(0, 1fr) 88px 96px'
      : '56px minmax(0, 1fr) 88px 80px 80px 92px';
    const listed = events.rows.filter((row) => row.kind !== 'OPENING' && row.kind !== 'SLICE');
    const startRow = events.startMinor !== null || events.startPending === true;
    return (
      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <Stack
          direction="row"
          data-card-header=""
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            p: '16px 20px',
            borderBottom: `1px solid ${tokens.line}`,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h3" component="h2">
              {t('forecast.eventsTitle')}
            </Typography>
            <Typography variant="caption" component="p" sx={{ color: tokens.text2 }}>
              {t('forecast.eventsSub')}
            </Typography>
          </Box>
          {events.drilled === true && events.onWiden !== undefined && (
            <Button variant="text" size="small" onClick={events.onWiden}>
              {t('cashflow.widen')}
            </Button>
          )}
          <Typography variant="caption" sx={{ color: tokens.text2 }}>
            {events.count ?? t('forecast.eventCount', { count: listed.length })}
          </Typography>
        </Stack>

        {md && (listed.length > 0 || startRow) && (
          <ListTableHeader columns={columns}>
            <span>{t('forecast.colDate')}</span>
            <span>{t('forecast.colEvent')}</span>
            <Box component="span" sx={{ textAlign: 'end' }}>
              {t('forecast.colAmount')}
            </Box>
            {balanceView ? (
              <Box component="span" sx={{ textAlign: 'end' }}>
                {t('envelope.colBalance')}
              </Box>
            ) : (
              <>
                <Box component="span" sx={{ textAlign: 'end' }}>
                  {t('forecast.colAllocated')}
                </Box>
                <Box component="span" sx={{ textAlign: 'end' }}>
                  {t('forecast.colFree')}
                </Box>
                <Box component="span" sx={{ textAlign: 'end' }}>
                  {t('forecast.colUnallocated')}
                </Box>
              </>
            )}
          </ListTableHeader>
        )}

        {startRow &&
          (md ? (
            <Box
              data-forecast-start=""
              sx={{
                display: 'grid',
                gridTemplateColumns: columns,
                columnGap: 2,
                alignItems: 'center',
                p: '10px 20px',
                bgcolor: tokens.surface2,
                borderBottom: `1px solid ${tokens.line}`,
              }}
            >
              <Box
                component="span"
                sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: tokens.text2 }}
              >
                {formatCivilDate(from, locale, DATE_SHORT)}
              </Box>
              <Box component="span" sx={{ fontSize: 13, fontWeight: 500 }}>
                {events.startLabel ?? t('forecast.unallocatedToday')}
              </Box>
              <span />
              {!balanceView && <span />}
              {!balanceView && <span />}
              <Box
                component="span"
                sx={{
                  textAlign: 'end',
                  fontSize: 13,
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                  color: balanceTone(events.startMinor),
                }}
              >
                {money(
                  events.startMinor,
                  balanceView ? events.amountCurrency : events.operatingCurrency,
                )}
              </Box>
            </Box>
          ) : (
            <Stack
              data-forecast-start=""
              sx={{
                gap: '3px',
                p: '14px 16px',
                bgcolor: tokens.surface2,
                borderBottom: `1px solid ${tokens.line}`,
              }}
            >
              <Box component="span" sx={{ fontSize: 13, fontWeight: 500 }}>
                {events.startLabel ?? t('forecast.unallocatedToday')}
              </Box>
              <Box component="span" sx={{ fontSize: 12, color: tokens.text2 }}>
                {formatCivilDate(from, locale, DATE_SHORT)}
              </Box>
              <Box
                component="span"
                sx={{
                  mt: '2px',
                  fontSize: 18,
                  fontVariantNumeric: 'tabular-nums',
                  color: balanceTone(events.startMinor),
                }}
              >
                {money(
                  events.startMinor,
                  balanceView ? events.amountCurrency : events.operatingCurrency,
                )}
              </Box>
            </Stack>
          ))}

        {listed.map((row, index) => {
          const open =
            events.onOpen !== undefined && row.seriesId !== null
              ? () => events.onOpen?.(row)
              : undefined;
          const name = row.payee ?? '—';
          const date = formatCivilDate(row.occursOn, locale, DATE_SHORT);
          const rowProps = open
            ? {
                component: 'button' as const,
                type: 'button' as const,
                onClick: open,
                'aria-label': t('forecast.openEvent', { name, date }),
              }
            : {};
          const gross = signedMoney(row.amountMinor, events.amountCurrency);
          const alloc = money(row.allocatedMinor, events.operatingCurrency);
          const free = signedMoney(row.freeMinor, events.operatingCurrency);
          const run = money(row.runningUnallocatedMinor, events.operatingCurrency);
          const bal = money(row.runningMinor, events.amountCurrency);
          const rowSx = {
            width: '100%',
            m: 0,
            border: 0,
            borderBottom: `1px solid ${tokens.line}`,
            bgcolor: 'transparent',
            color: tokens.text,
            font: 'inherit',
            textAlign: 'start',
            cursor: open ? 'pointer' : 'default',
            '&:hover': { bgcolor: tokens.hover },
            '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: -2 },
          } as const;
          return md ? (
            <Box
              key={`${row.occursOn}-${row.seriesId ?? 'x'}-${index}`}
              {...rowProps}
              data-forecast-row=""
              sx={{
                ...rowSx,
                display: 'grid',
                gridTemplateColumns: columns,
                columnGap: 2,
                alignItems: 'center',
                p: '9px 20px',
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: 12,
                  fontVariantNumeric: 'tabular-nums',
                  color: tokens.text2,
                  whiteSpace: 'nowrap',
                }}
              >
                {date}
              </Box>
              <Box
                component="span"
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {name}
              </Box>
              <Box
                component="span"
                sx={{
                  textAlign: 'end',
                  fontSize: 13,
                  fontVariantNumeric: 'tabular-nums',
                  color: amountTone(row.amountMinor),
                  whiteSpace: 'nowrap',
                }}
              >
                {gross}
              </Box>
              {balanceView ? (
                <Box
                  component="span"
                  sx={{
                    textAlign: 'end',
                    fontSize: 13,
                    fontVariantNumeric: 'tabular-nums',
                    color: balanceTone(row.runningMinor),
                    whiteSpace: 'nowrap',
                  }}
                >
                  {bal}
                </Box>
              ) : (
                <>
                  <Box
                    component="span"
                    sx={{
                      textAlign: 'end',
                      fontSize: 12,
                      fontVariantNumeric: 'tabular-nums',
                      color: tokens.text2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {alloc}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      textAlign: 'end',
                      fontSize: 13,
                      fontWeight: 500,
                      fontVariantNumeric: 'tabular-nums',
                      color: amountTone(row.freeMinor),
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {free}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      textAlign: 'end',
                      fontSize: 13,
                      fontVariantNumeric: 'tabular-nums',
                      color: balanceTone(row.runningUnallocatedMinor),
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {run}
                  </Box>
                </>
              )}
            </Box>
          ) : (
            <Stack
              key={`${row.occursOn}-${row.seriesId ?? 'x'}-${index}`}
              {...rowProps}
              data-forecast-row=""
              sx={{ ...rowSx, gap: 1, p: '14px 16px' }}
            >
              <Box>
                <Box component="div" sx={{ fontSize: 15, fontWeight: 500 }}>
                  {name}
                </Box>
                <Box component="div" sx={{ mt: '2px', fontSize: 12, color: tokens.text2 }}>
                  {date}
                </Box>
              </Box>
              <Stack direction="row" sx={{ gap: 2, flexWrap: 'wrap' }}>
                {(
                  (balanceView
                    ? [
                        ['colAmount', gross, amountTone(row.amountMinor), 400],
                        ['colBalance', bal, balanceTone(row.runningMinor), 500],
                      ]
                    : [
                        ['colAmount', gross, amountTone(row.amountMinor), 400],
                        ['colAllocated', alloc, tokens.text2, 400],
                        ['colFree', free, amountTone(row.freeMinor), 500],
                        ['colUnallocated', run, balanceTone(row.runningUnallocatedMinor), 500],
                      ]) as readonly (readonly [string, ReactNode, string, number])[]
                ).map(([key, value, color, weight]) => (
                  <Box
                    key={key}
                    component="span"
                    sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}
                  >
                    <Box component="span" sx={{ typography: 'micro', color: tokens.text2 }}>
                      {key === 'colBalance' ? t('envelope.colBalance') : t(`forecast.${key}`)}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 14,
                        fontWeight: weight,
                        fontVariantNumeric: 'tabular-nums',
                        color,
                      }}
                    >
                      {value}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Stack>
          );
        })}

        {listed.length === 0 && (
          <Typography
            data-forecast-empty=""
            sx={{
              p: '26px 20px',
              fontSize: 13,
              lineHeight: 1.6,
              maxWidth: 520,
              color: tokens.text2,
            }}
          >
            {events.empty ?? t('forecast.eventsEmpty')}
          </Typography>
        )}
      </Paper>
    );
  }

  return (
    <Stack component="section" aria-label={label} sx={{ gap: 2.5 }} data-forecast-card="">
      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <Stack
          direction="row"
          data-forecast-toolbar=""
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px 20px',
            p: '14px 20px',
            borderBottom: `1px solid ${tokens.line}`,
          }}
        >
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Overline>{t('cashflow.horizon')}</Overline>
            <SegmentedControl<`${HorizonMonths}`>
              aria-label={t('cashflow.horizon')}
              value={`${horizon}`}
              options={HORIZONS.map((months) => ({
                value: `${months}` as const,
                label: t('cashflow.horizonMonths', { count: months }),
              }))}
              onChange={(next) => setUrl({ h: Number(next) as HorizonMonths })}
            />
          </Stack>
          {filter}
          <Box sx={{ flex: 1 }} />
          <Typography
            variant="caption"
            data-forecast-window=""
            sx={{ color: tokens.textLabel('surface'), whiteSpace: 'nowrap' }}
          >
            {t('forecast.window', {
              from: formatCivilDate(from, locale),
              to: formatCivilDate(to, locale),
            })}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          data-forecast-kpis=""
          sx={{ flexWrap: 'wrap', gap: '12px 32px', p: '18px 20px 4px' }}
        >
          {kpis.map((kpi) => (
            <Box key={kpi.id} data-kpi={kpi.id}>
              <Overline>{kpi.label}</Overline>
              <Box sx={{ lineHeight: 1.2 }}>{kpi.value}</Box>
              {kpi.note !== undefined && kpi.note !== null && (
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ mt: '2px', fontVariantNumeric: 'tabular-nums' }}
                >
                  {kpi.note}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>

        <Box sx={{ p: '8px 20px 6px' }}>
          <ProjectionChart
            from={from}
            to={to}
            series={chart.series}
            events={chart.events}
            register={chart.register}
            currency={chart.currency}
            minimum={chart.minimum}
            hiddenIds={hidden}
            {...(md ? { onToggleSeries: toggleSeries } : {})}
            {...(chart.onDrill !== undefined ? { onDrill: chart.onDrill } : {})}
          />
        </Box>
        {note !== undefined && note !== null && note !== '' && (
          <Typography
            variant="caption"
            component="p"
            data-forecast-note=""
            sx={{
              p: '10px 20px 14px',
              maxWidth: 760,
              lineHeight: 1.6,
              color: tokens.textLabel('surface'),
              textWrap: 'pretty',
            }}
          >
            {note}
          </Typography>
        )}
      </Paper>

      {fromToday && (
        <Typography
          variant="caption"
          component="p"
          data-from-today=""
          sx={{ m: 0, color: tokens.textLabel('bg') }}
        >
          {t('forecast.fromToday')}
        </Typography>
      )}
      {list ?? (events !== undefined && eventsList(events))}
    </Stack>
  );
}
